/** Grupos de cotización proveedor → escandallo */
export const RFQ_QUOTE_GROUPS = [
  { grupoId: 1, label: "MAIN FABRICS" },
  { grupoId: 2, label: "BODY" },
  { grupoId: 3, label: "TRIMS" },
  { grupoId: 4, label: "WORKMANSHIP" },
  { grupoId: 5, label: "FINISHING" },
  { grupoId: 6, label: "LABEL, PACK, ALARM" },
  { grupoId: 7, label: "OTHERS" },
  { grupoId: 10, label: "PADDING" },
  { grupoId: 11, label: "ACCESSORIES" },
  { grupoId: 14, label: "STYLE COST" },
  { grupoId: 16, label: "INTERLINING" },
  { grupoId: 17, label: "LINNING" },
];

export const RFQ_QUOTE_GROUP_IDS = RFQ_QUOTE_GROUPS.map((g) => g.grupoId);
export const RFQ_QUOTE_CURRENCIES = ["EUR", "USD", "GBP", "CNY", "HKD"];

export function parseRfqDecimal(raw) {
  const n = Number(String(raw ?? "").replace(",", ".").trim());
  return Number.isFinite(n) ? n : null;
}

/** Importe de línea = units × unit cost */
export function lineExtendedTotal(line) {
  const u = parseRfqDecimal(line?.un ?? "1");
  const c = parseRfqDecimal(line?.coste);
  if (u == null || c == null || u <= 0 || c <= 0) return null;
  return u * c;
}

export function formatRfqAmount(n) {
  if (!Number.isFinite(n)) return "—";
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 10000) / 10000);
}

export function sumLineTotalsByCurrency(lines) {
  const map = {};
  for (const l of lines || []) {
    const ext = lineExtendedTotal(l);
    if (ext == null) continue;
    const mon = String(l.moneda || "EUR").toUpperCase();
    map[mon] = (map[mon] || 0) + ext;
  }
  return map;
}

export function formatLineTotalsLabel(map) {
  const keys = Object.keys(map);
  if (!keys.length) return "—";
  return keys.map((k) => `${formatRfqAmount(map[k])} ${k}`).join(" · ");
}

export function formatWeightPct(part, total) {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) {
    return "—";
  }
  const pct = (part / total) * 100;
  if (Math.abs(pct - Math.round(pct)) < 0.05) return `${Math.round(pct)}%`;
  return `${Math.round(pct * 10) / 10}%`;
}

export function lineWeightPct(line, totalsByMon) {
  const ext = lineExtendedTotal(line);
  if (ext == null) return "—";
  const mon = String(line?.moneda || "EUR").toUpperCase();
  return formatWeightPct(ext, totalsByMon?.[mon]);
}

export function formatLineExtendedLabel(line) {
  const ext = lineExtendedTotal(line);
  if (ext == null) return "—";
  return `${formatRfqAmount(ext)} ${line?.moneda || "EUR"}`;
}

export function emptyRfqQuoteRow(grupoId, moneda = "EUR") {
  return {
    key: `n-${grupoId}-${Math.random().toString(36).slice(2, 8)}`,
    grupoId,
    subgrupoId: "",
    un: "1",
    moneda: String(moneda || "EUR").toUpperCase() || "EUR",
    coste: "",
  };
}

export function parseRfqQuoteLinesJson(raw) {
  let arr = raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .map((row, idx) => ({
      key: `s-${idx}-${row?.grupoId}-${row?.subgrupoId}`,
      grupoId: Number(row?.grupoId ?? row?.ESCANDALLO_GRUPOS),
      subgrupoId: Number(row?.subgrupoId ?? row?.ESCANDALLO_SUBGRUPO) || "",
      un: row?.un ?? row?.Un ?? "1",
      moneda: String(row?.moneda ?? row?.MonedaCoste ?? "EUR").toUpperCase(),
      coste:
        row?.coste != null || row?.Coste != null
          ? String(row.coste ?? row.Coste)
          : "",
    }))
    .filter((r) => RFQ_QUOTE_GROUPS.some((g) => g.grupoId === r.grupoId));
}

/** Una fila por grupo (legacy). Preferir `rowsFromStoredOrDefaults` para multi-línea. */
export function mergeRfqQuoteRowsFromStored(storedLines, defaultMoneda = "EUR") {
  const byGrupo = new Map(
    (storedLines || []).map((l) => [Number(l.grupoId), l]),
  );
  return RFQ_QUOTE_GROUPS.map((g) => {
    const hit = byGrupo.get(g.grupoId);
    if (!hit) return emptyRfqQuoteRow(g.grupoId, defaultMoneda);
    return {
      key: hit.key || `g-${g.grupoId}`,
      grupoId: g.grupoId,
      subgrupoId: hit.subgrupoId || "",
      un: hit.un != null ? String(hit.un) : "1",
      moneda: String(hit.moneda || defaultMoneda).toUpperCase() || "EUR",
      coste: hit.coste != null && hit.coste !== "" ? String(hit.coste) : "",
    };
  });
}

/** Filas reales del portal (N líneas) o plantilla de grupos vacíos. */
export function rowsFromStoredOrDefaults(storedLines, defaultMoneda = "EUR") {
  const list = Array.isArray(storedLines) ? storedLines : [];
  const withCost = list.filter((l) => String(l.coste ?? "").trim() !== "");
  if (withCost.length) {
    return withCost.map((hit, idx) => ({
      key: hit.key || `s-${idx}-${hit.grupoId}-${hit.subgrupoId}`,
      grupoId: Number(hit.grupoId),
      subgrupoId: hit.subgrupoId || "",
      un: hit.un != null ? String(hit.un) : "1",
      moneda: String(hit.moneda || defaultMoneda).toUpperCase() || "EUR",
      coste: String(hit.coste),
    }));
  }
  if (list.length) {
    return list.map((hit, idx) => ({
      key: hit.key || `s-${idx}-${hit.grupoId}`,
      grupoId: Number(hit.grupoId) || RFQ_QUOTE_GROUPS[0].grupoId,
      subgrupoId: hit.subgrupoId || "",
      un: hit.un != null ? String(hit.un) : "1",
      moneda: String(hit.moneda || defaultMoneda).toUpperCase() || "EUR",
      coste: hit.coste != null && hit.coste !== "" ? String(hit.coste) : "",
    }));
  }
  return RFQ_QUOTE_GROUPS.map((g) => emptyRfqQuoteRow(g.grupoId, defaultMoneda));
}
